import { Link } from 'lucide-react'
import SecureInput from '../../../ui/SecureInput/SecureInput'
import { CreateIdpProviderDto } from '../../../types/types'
import { sanitizeJsonInput, MAX_LENGTHS } from '../../../utils/security'
import styles from '../SSOConfiguration.module.css'

interface SamlProviderFormProps {
  providerForm: CreateIdpProviderDto
  onFieldChange: (field: keyof CreateIdpProviderDto, value: string) => void
  onConfigChange: (key: string, value: string) => void
}

export default function SamlProviderForm({
  providerForm,
  onFieldChange,
  onConfigChange
}: SamlProviderFormProps) {
  const handleTextareaChange = (key: string, value: string) => {
    const sanitizedValue = sanitizeJsonInput(value)
    onConfigChange(key, sanitizedValue)
  }

  return (
    <div className={styles.providerFormGrid}>
      <div className={styles.formField}>
        <label className={styles.fieldLabel}>
          Name <span className={styles.required}>*</span>
        </label>
        <SecureInput
          fieldType="name"
          label=""
          value={providerForm.name}
          onChange={(value) => onFieldChange('name', value)}
          placeholder="e.g. SAML Identity Provider"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel}>
          Description
        </label>
        <SecureInput
          fieldType="text"
          label=""
          value={providerForm.description || ''}
          onChange={(value) => onFieldChange('description', value)}
          placeholder="Optional description"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel}>
          SSO URL <span className={styles.required}>*</span>
        </label>
        <SecureInput
          fieldType="url"
          label=""
          icon={<Link size={16} />}
          value={providerForm.config?.ssoUrl || ''}
          onChange={(value) => onConfigChange('ssoUrl', value)}
          placeholder="https://your-idp.com/sso/saml"
          className={styles.input}
        />
        <p className={styles.fieldDescription}>SAML Identity Provider SSO URL</p>
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel}>
          Entity ID <span className={styles.required}>*</span>
        </label>
        <SecureInput
          fieldType="text"
          label=""
          value={providerForm.config?.entityId || ''}
          onChange={(value) => onConfigChange('entityId', value)}
          placeholder="urn:your-company:saml:sp"
          className={styles.input}
        />
        <p className={styles.fieldDescription}>SAML Service Provider Entity ID</p>
      </div>

      <div className={styles.formField} style={{ gridColumn: '1 / -1' }}>
        <label className={styles.fieldLabel}>X.509 Certificate</label>
        <textarea
          value={providerForm.config?.x509Cert || ''}
          onChange={(e) => handleTextareaChange('x509Cert', e.target.value)}
          placeholder="-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"
          className={styles.textarea}
          rows={6}
          maxLength={MAX_LENGTHS.text}
        />
        <p className={styles.fieldDescription}>Identity Provider X.509 certificate for signature verification</p>
      </div>

      <div className={styles.formField} style={{ gridColumn: '1 / -1' }}>
        <label className={styles.fieldLabel}>Attribute Mapping</label>
        <textarea
          value={providerForm.config?.attributeMapping || ''}
          onChange={(e) => handleTextareaChange('attributeMapping', e.target.value)}
          placeholder='{\n  "email": "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",\n  "first_name": "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"\n}'
          className={styles.textarea}
          rows={6}
          maxLength={MAX_LENGTHS.text}
        />
        <p className={styles.fieldDescription}>JSON mapping of user attributes from SAML response</p>
      </div>
    </div>
  )
}
